import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';

const HomePage = () => {
    const { keycloak, initialized } = useKeycloak();
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavLinkClick = (section) => {
        setActiveSection(section);
        setIsMenuOpen(false); // Close mobile menu on link click
    };

    // Functions to handle Keycloak redirects with loading state
    const handleSignIn = () => {
        setIsLoading(true);
        keycloak.login();
    };

    const handleSignUp = () => {
        setIsLoading(true);
        keycloak.register();
    };

    const heroStyle = {
        backgroundImage: `url(/images/home.png)`,
    };

    return (
        <div>
            {/* Header */}
            <header className={`w-full py-2 px-5 flex items-center justify-between text-white z-50 transition-all duration-300 ${scrolled ? 'fixed top-0 bg-black bg-opacity-85 shadow-lg' : 'absolute top-0 bg-black bg-opacity-50'}`}>
                <div className="flex items-center">
                    <img src="/images/logo.png" alt="Sahaj Gyan" className="h-14" />
                </div>
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-2xl cursor-pointer">
                    ☰
                </button>

                {/* Desktop Nav */}
                <nav className="hidden md:flex gap-8 items-center">
                    <a href="#home" onClick={() => handleNavLinkClick('home')} className={`hover:text-cyan-300 transition-colors duration-300 ${activeSection === 'home' ? 'border-b-2 border-white' : ''}`}>
                        Home
                    </a>
                    <a href="#pillars" onClick={() => handleNavLinkClick('pillars')} className={`hover:text-cyan-300 transition-colors duration-300 ${activeSection === 'pillars' ? 'border-b-2 border-white' : ''}`}>
                        Pillars
                    </a>
                </nav>

                <button
                    onClick={handleSignIn}
                    className="hidden md:block bg-transparent text-white border border-white px-4 py-2 font-bold rounded-md hover:bg-white hover:text-black transition-colors duration-300"
                    disabled={isLoading}
                >
                    {isLoading ? 'Loading...' : 'Sign In'}
                </button>
            </header>

            {/* Mobile Menu */}
            <nav className={`fixed inset-0 bg-black bg-opacity-90 flex-col items-center justify-center space-y-8 z-40 transition-transform duration-300 ${isMenuOpen ? 'flex' : 'hidden'}`}>
                <button onClick={() => setIsMenuOpen(false)} className="absolute top-4 right-4 text-white text-3xl">✖</button>
                <a href="#home" onClick={() => handleNavLinkClick('home')} className="text-white text-2xl hover:text-cyan-300">
                    Home
                </a>
                <a href="#pillars" onClick={() => handleNavLinkClick('pillars')} className="text-white text-2xl hover:text-cyan-300">
                    Pillars
                </a>
                <button
                    onClick={() => { handleSignIn(); setIsMenuOpen(false); }}
                    className="bg-transparent text-white px-6 py-3 border border-white rounded-md font-bold hover:bg-white hover:text-black transition-colors duration-300 text-lg"
                    disabled={isLoading}
                >
                    {isLoading ? 'Loading...' : 'Sign In'}
                </button>
            </nav>

            {/* Hero Section */}
            <section id="home" className="flex items-center justify-center min-h-screen p-12 bg-cover bg-center" style={heroStyle}>
                <div className="bg-black bg-opacity-75 text-white p-10 max-w-xl rounded-xl text-left ml-auto">
                    <h2 className="text-4xl font-bold mb-4">Learning simplified</h2>
                    <p className="mb-4">
                        Sahaj Gyan envisions learning as a living tree — rooted in clarity, growing with curiosity, and bearing the fruits of understanding. We believe that when knowledge is planted in simple soil, it takes root with ease and grows into wisdom that lasts a lifetime.
                    </p>
                    <br />
                    <p className="mb-4">
                        Through thoughtful, visual learning, we aim to nourish minds, spark wonder, and let learning unfold as naturally as leaves to the sun.
                    </p>
                    <hr className="my-5 border-gray-500" />
                    <p>
                        <button onClick={handleSignUp} className="text-cyan-400 hover:underline" disabled={isLoading}>
                            {isLoading ? 'Loading...' : 'Sign up'}
                        </button> to create an account or <button onClick={handleSignIn} className="text-cyan-400 hover:underline" disabled={isLoading}>
                            {isLoading ? 'Loading...' : 'Sign in'}
                        </button> to access learning content.
                    </p>
                </div>
            </section>

            {/* Pillars Section */}
            <section id="pillars" className="py-20 px-8 text-center bg-white">
                <h2 className="text-4xl font-bold mb-10 text-gray-800">Our Pillars</h2>
                <div className="flex flex-wrap justify-center gap-8">
                    {/* Tutors */}
                    <div className="flex-1 min-w-[300px] p-8 rounded-2xl shadow-lg bg-[#B3CDE5] transition-transform duration-300 hover:scale-105 text-left">
                        <img src="/images/tutor.png" alt="Tutor" className="w-16 h-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-semibold mb-4">Tutors</h3>
                        <p className="mb-4 text-gray-700">
                            With Sahaj Gyan, aspiring tutors can focus on what they do best — teaching with passion and creativity. The platform empowers educators to deliver lessons in a fun, intuitive way using features like{" "}
                            <strong>Interactive Labs</strong> and{" "}
                            <strong>Thought-Provoking DIY Experiments</strong> that spark curiosity and deepen understanding.
                        </p>
                        <p className="text-gray-700">
                            Beyond the classroom, Sahaj Gyan streamlines administrative tasks — from onboarding students and crafting test plans, to conducting assessments, tracking learning progress, and managing earnings — all through a seamless, digitized workflow.
                        </p>
                    </div>

                    {/* Parents */}
                    <div className="flex-1 min-w-[300px] p-8 rounded-2xl shadow-lg bg-[#BCE3D0] transition-transform duration-300 hover:scale-105 text-left">
                        <img src="/images/parent.png" alt="Parent" className="w-16 h-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-semibold mb-4">Parents</h3>
                        <p className="mb-4 text-gray-700">
                            Parents can take an active role in their child’s learning journey by creating custom test plans, administering assessments, and closely tracking progress.
                        </p>
                        <p className="text-gray-700">
                            With Sahaj Gyan, they gain meaningful insights into their child’s strengths and areas for improvement — helping them support growth with clarity and confidence.
                        </p>
                    </div>

                    {/* Students */}
                    <div className="flex-1 min-w-[300px] p-8 rounded-2xl shadow-lg bg-[#D8C8E9] transition-transform duration-300 hover:scale-105 text-left">
                        <img src="/images/student.png" alt="Student" className="w-16 h-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-semibold mb-4">Students</h3>
                        <p className="mb-4 text-gray-700">
                            Students can take tests assigned by tutors or parents and receive clear, personalized insights into their strengths and areas for improvement.
                        </p>
                        <p className="text-gray-700">
                            These insights empower them to learn smarter and grow with confidence.
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="text-center py-10 text-sm text-gray-600 bg-gray-100">
                © 2025 Sahaj Gyan India Pvt. Ltd. All rights reserved.
            </footer>

            {/* Loading Overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[1000]">
                    <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-xl">
                        <svg className="animate-spin h-10 w-10 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-4 text-lg font-semibold text-gray-700">Redirecting to login...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomePage;